import { useState, useRef } from "react";

/**
 * ToolDialog — Blank draggable dialog shell.
 *
 * Use this as the wrapper for any tool UI.
 * Put your custom content as children.
 *
 * @param {string}   title    - Shown in the AppBar
 * @param {boolean}  open     - Controls visibility
 * @param {Function} onClose  - Called when X is clicked
 * @param {Object}   map      - Map instance (available to children via props or context)
 * @param {Object}   layers   - Geometry layers
 * @param {React.ReactNode} children - Your custom tool UI
 *
 * @example
 * <ToolDialog title="My Tool" open={open} onClose={onClose} map={map} layers={layers}>
 *   <MyToolContent />
 * </ToolDialog>
 */
const ToolDialog = ({ title, open, onClose, children }) => {
  const [pos, setPos] = useState({ x: 10, y: 10 });

  const handleMouseDown = (e) => {
    const startX = e.clientX;
    const startY = e.clientY;
    const initX = pos.x;
    const initY = pos.y;

    const onMove = (ev) => {
      setPos({
        x: initX + (ev.clientX - startX),
        y: initY + (ev.clientY - startY),
      });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: pos.x,
        top: pos.y,
        width: 320,
        zIndex: 1400,
        borderRadius: 4,
        overflow: "hidden",
        boxShadow:
          "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
        background: "#fff",
        userSelect: "none",
      }}
    >
      {/* ── AppBar (drag handle) ── */}
      <div
        onMouseDown={handleMouseDown}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0 6px 0 16px",
          background: "#1976d2",
          minHeight: 44,
          cursor: "move",
          flexShrink: 0,
        }}
      >
        <span style={{
          fontFamily: "Roboto, Arial, sans-serif",
          fontSize: 15, fontWeight: 500,
          color: "#fff", flex: 1,
          pointerEvents: "none",
        }}>
          {title}
        </span>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          title="Close"
          style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "6px", color: "#fff",
            display: "flex", alignItems: "center", borderRadius: "50%",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "none"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* ── Empty content area — put your UI here ── */}
      <div style={{ userSelect: "text"}}>
        {children}
      </div>
    </div>
  );
};

export default ToolDialog;

